import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { resolveBuildId, userDisplayName } from "common-fe";
import { HEARTBEAT_INTERVAL_MS } from "score-common";
import { useScoreApi } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { getPresenceLocation } from "../store/presenceLocation";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import { INACTIVITY_TIMEOUT_MS } from "./useInactivityRedirect";

// #2364 (scribe-presence workstream, card C) — the FE SENDER half of the
// heartbeat contract score-api's putHeartbeat documents (apps/score-api/src/
// lib/presence.js file header): "the fire-and-forget guarantee lives on the
// SENDER side (#2364, scribe-fe), not here: this module's putHeartbeat DOES
// propagate a DynamoDB failure to its caller". Everything below exists to
// make that promise true from this hook outward:
//
//   - Calls the generated PresenceApi client DIRECTLY (scoreApi.presence,
//     api/scoreApi.js), never through ScoreContext's apiAdapter/syncEngine
//     — a failed heartbeat must never enter the scoring outbox (card's own
//     rule) or be retried by the sync engine's backoff.
//   - Every failure — a synchronous throw building the request, a rejected
//     promise (4xx/5xx/network) — is swallowed right here. Nothing in this
//     hook ever throws into a render or an unhandled rejection, shows a
//     toast/banner, or retries. In particular this NEVER touches
//     syncEngine.setAuthFailureHandler / signinSilent: a 401 on a heartbeat
//     is a silent no-op, so it can never set CognitoAuthProvider's
//     auth.error and trip AuthGate's full-screen unmount (#1289) while a
//     scribe is mid-ride.
//   - The interval itself never blocks/delays anything: setInterval fires
//     independently of React's render/commit cycle and of every scoring
//     write path.
//   - Tab hidden, or idle past INACTIVITY_TIMEOUT_MS (the SAME threshold
//     useInactivityRedirect already uses to route a forgotten kiosk to
//     standby — reused here rather than inventing a second magic number),
//     suspends sending outright: "stale presence is worse than no
//     presence" (card description). The row simply ages past
//     PRESENCE_TTL_SECONDS and disappears from every reader — no explicit
//     "gone" signal is sent or needed.
//   - Only sends once a valid seat is known (getPresenceLocation, set by
//     ScoringForm — see that store's own header comment for why this is the
//     ONLY screen where ring/classId/rideId/seat are simultaneously known:
//     score-api's bodySchema requires `seat`). Browsing ClassList/RideList/
//     ClassGrid has nothing valid to report, so the interval simply no-ops
//     on every tick until a ride is open.
//
// Mounted exactly once (App.jsx, inside ScoreProvider) — NOT per-screen —
// so the interval survives route changes between rides/classes; only the
// location it reads changes.
export function usePresenceHeartbeat() {
  const scoreApi = useScoreApi();
  const auth = useAuth();
  const { selectedShow } = useScoreContext();
  const showId = selectedShow?.id ?? null;
  const isAuthenticated = Boolean(auth.isAuthenticated);
  const scribeName = userDisplayName(auth.user?.profile) || null;
  const lastActivityRef = useRef(Date.now());

  // Same lightweight activity signal useInactivityRedirect watches
  // (pointer/keyboard/touch/wheel), tracked independently here so this
  // hook's idle decision never depends on that hook's route-navigation
  // side effect actually having fired yet.
  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel"];
    for (const evt of events) {
      window.addEventListener(evt, markActive, { passive: true });
    }
    return () => {
      for (const evt of events) {
        window.removeEventListener(evt, markActive);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !showId) return undefined;

    const send = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT_MS) return;
      const location = getPresenceLocation();
      if (!location || !location.seat) return;
      try {
        scoreApi.presence
          .postHeartbeat({
            showId,
            instanceId: getPresenceInstanceId(),
            ring: location.ring ?? null,
            classId: location.classId ?? null,
            rideId: location.rideId ?? null,
            seat: location.seat,
            scribeName,
            appBuild: resolveBuildId(),
          })
          .catch(() => {
            // Fire-and-forget: a failed heartbeat is silent, full stop.
          });
      } catch {
        // A synchronous throw building/sending the request is just as
        // silent — never propagate.
      }
    };

    const id = setInterval(send, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [scoreApi, showId, isAuthenticated, scribeName]);
}

export default usePresenceHeartbeat;
